import { useCallback } from 'react';

import { canWebsiteAction, type WebsitePermission } from './rbac';

import type { UserRole } from '@utazzo/shared';

import { useMyEffectivePermissions } from '@/features/my-profile/use-my-profile';
import { useAuthStore } from '@/stores/auth-store';

/**
 * Reads the signed-in user's role and effective permission set, returning a
 * stable capability check for website-CMS write surfaces.
 */
export function usePermissions(): {
  role: UserRole | null;
  can: (action: WebsitePermission) => boolean;
} {
  const role = useAuthStore((s) => s.user?.role ?? null);
  const effectivePermissions = useMyEffectivePermissions();
  const can = useCallback(
    (action: WebsitePermission) => canWebsiteAction(role, action, effectivePermissions),
    [role, effectivePermissions],
  );
  return { role, can };
}

/**
 * Convenience selector for a single permission. Equivalent to
 * `usePermissions().can(action)` but with a smaller subscription footprint.
 */
export function useCan(action: WebsitePermission): boolean {
  const role = useAuthStore((s) => s.user?.role ?? null);
  const effectivePermissions = useMyEffectivePermissions();
  return canWebsiteAction(role, action, effectivePermissions);
}
